import { useState, useRef } from 'react';
import { useEditorImageUpload } from '@/hooks/useEditorImageUpload';

interface ImageUploaderProps {
  currentUrl: string;
  onChange: (url: string) => void;
  placeholder?: string;
  className?: string;
  previewHeight?: string;
  maxWidth?: number;
  /** 分类上传：home | services | knowledge | cases | contact | common | admin */
  category?: string;
}

function compressImage(file: File, maxWidth: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('读取文件失败'));

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas 初始化失败')); return; }

      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > maxWidth) {
        h = Math.round(h * (maxWidth / w));
        w = maxWidth;
      }

      canvas.width = w;
      canvas.height = h;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      // Aggressive JPEG quality 0.5 for safe JSON payload size
      const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
      resolve(dataUrl);
    };
    img.onerror = () => reject(new Error('图片加载失败'));
    reader.readAsDataURL(file);
  });
}

export default function ImageUploader({
  currentUrl,
  onChange,
  placeholder = '点击上传 / 粘贴 URL',
  className = '',
  previewHeight = 'h-32',
  maxWidth = 1600,
  category,
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useEditorImageUpload();

  const validateAndUpload = async (file: File) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setUploadError('仅支持 JPG、PNG、WebP、GIF 格式');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('图片原始大小不能超过 2MB');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      if (category) {
        // 分类上传：走后端 /api/upload-image，按分类落盘到 uploads/<category>/
        const result = await uploadFile(file, category);
        if (result.error) {
          setUploadError(result.error);
        } else if (result.url) {
          onChange(result.url);
        }
      } else {
        // 无分类时兜底：base64 直接存库（保持旧行为）
        const dataUrl = await compressImage(file, maxWidth);
        // Check raw JSON payload char length (base64 string as-is in JSON)
        const maxChars = 150 * 1024; // 150KB raw string limit
        if (dataUrl.length > maxChars) {
          setUploadError(`压缩后仍 ${Math.round(dataUrl.length / 1024)}KB，超过 150KB 安全上限，请换更小的图片`);
          return;
        }
        onChange(dataUrl);
      }
    } catch (err: any) {
      setUploadError(`图片处理失败：${err?.message || String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    validateAndUpload(file);
    e.target.value = '';
  };

  const handleUrlConfirm = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (!/^https?:\/\//.test(trimmed)) {
      setUploadError('URL 必须以 http:// 或 https:// 开头');
      return;
    }
    onChange(trimmed);
    setUrlInput('');
    setUploadError('');
  };

  return (
    <div className={className}>
      {currentUrl ? (
        <div className="relative w-full rounded-xl overflow-hidden bg-gray-100 mb-2 group">
          <img src={currentUrl} alt="预览" className={`w-full ${previewHeight} object-cover`} />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-white text-[#1a3a5c] text-xs px-3 py-1.5 rounded-full cursor-pointer font-medium hover:bg-[#c9a96e] hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="ri-upload-cloud-line mr-1"></i>重新上传
            </button>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setUploadError('');
              }}
              className="bg-white text-red-500 text-xs px-3 py-1.5 rounded-full cursor-pointer font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1"></i>移除
            </button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <i className="ri-loader-4-line text-white text-xl animate-spin"></i>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl py-5 text-center text-gray-400 text-xs hover:border-[#c9a96e] hover:text-[#c9a96e] cursor-pointer transition-colors disabled:opacity-60"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-base"></i>
                压缩处理中...
              </span>
            ) : (
              <span>
                <i className="ri-image-add-line text-2xl block mb-1"></i>
                {placeholder}
              </span>
            )}
          </button>
          <div className="flex gap-2">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setUploadError(''); }}
              placeholder="粘贴图片 URL..."
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#c9a96e]"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUrlConfirm(); } }}
            />
            <button
              type="button"
              onClick={handleUrlConfirm}
              className="bg-[#c9a96e] text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#b8944f]"
            >
              确认
            </button>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
      />

      {uploadError && (
        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {uploadError}
        </p>
      )}
    </div>
  );
}